import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { Image, PriceDisplay } from "@/components/atoms";
import type { PreorderCartItemProps } from "./PreorderCartItem.types";
import { QuantityControl } from "../QuantityControl";
import { imagorService } from "@/helpers/imagorService";
import { itemPrice } from "@/core/orders/utils";

const PreorderCartItem: React.FC<PreorderCartItemProps> = ({
  item,
  imageUrl,
  onQuantityChange,
  disabled = false,
  maxQuantity,
}) => {
  const theme = useTheme();

  // Снимок несёт imageId, а не URL: адрес резолвит useCartImageUrls по точке
  // заказа и приезжает сюда пропсом.
  const rawProductImage = imageUrl || null;
  const productImage = rawProductImage
    ? imagorService.transformUrl(rawProductImage, {
        width: 50,
        height: 50,
        smart: true,
      })
    : null;

  // Используем name из item, если есть, иначе из productSnapshot
  const productName = item.name || item.productSnapshot.name || "";

  // Варианты читаются из СНИМКА: legacy-поле productVariantProperties после
  // переезда на REST не заполняет никто, и варианты в корзине пропали бы.
  const variantProperties = item.productSnapshot.variantProperties;

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1.5,
        paddingBottom: 1.5,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      {/* Product Image */}
      <Image
        src={productImage || ""}
        alt={productName}
        width={50}
        height={50}
        showLoadingState={true}
        borderRadius="10px"
      />

      {/* Product Details */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          minWidth: 0,
        }}
      >
        {/* Name and Description */}
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: "16px",
              color: theme.palette.text.primary,
              lineHeight: 1.1,
              marginBottom: 0.5,
            }}
          >
            {productName}
          </Typography>

          {/* Variants - отображаем из productVariantProperties */}
          {variantProperties.length > 0 && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "13px",
                color: theme.palette.text.primary,
                opacity: 0.7,
                lineHeight: 1.1,
              }}
            >
              {variantProperties
                .map((variant) => variant.variantPropertyValueName)
                .join(", ")}
            </Typography>
          )}

          {/* Options - выбранные опции из item.options */}
          {item.options.length > 0 && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "13px",
                color: theme.palette.text.primary,
                opacity: 0.7,
                lineHeight: 1.1,
              }}
            >
              {item.options
                .map((option) =>
                  option.quantity > 1
                    ? `${option.name} × ${option.quantity}`
                    : option.name,
                )
                .join(", ")}
            </Typography>
          )}

          {/* Edit Button */}
          {/* <Button
            onClick={onEditItem}
            disabled={disabled}
            startIcon={<EditIcon sx={{ fontSize: 14 }} />}
            sx={{
              marginTop: 0.5,
              padding: 0,
              minWidth: "auto",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              color: theme.palette.text.primary,
              lineHeight: 1.4,
              "&:hover": {
                backgroundColor: "transparent",
                textDecoration: "underline",
              },
            }}
          >
            Изменить
          </Button> */}
        </Box>
      </Box>

      {/* Price and Quantity */}
      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: 1,
        }}
      >
        {/* Price */}
        <Typography
          variant="body1"
          sx={{
            fontWeight: 600,
            fontSize: 16,
            textAlign: "right",
          }}
        >
          {/* item.price — цена ТОВАРА без опций (см. core/orders/utils.ts);
              строка обязана включать опции, иначе позиция с выбранными
              опциями отображается дешевле, чем стоит на самом деле. */}
          <PriceDisplay value={itemPrice(item) * item.quantity} />
        </Typography>

        {/* Quantity Control */}
        {item.quantity > 0 && (
          <QuantityControl
            quantity={item.quantity}
            onQuantityChange={onQuantityChange}
            disabled={disabled}
            size="small"
            min={0}
            max={maxQuantity}
            aria-label={`Quantity for ${productName}`}
          />
        )}
      </Box>
    </Box>
  );
};

export default PreorderCartItem;
